<template>
  <aha-modal
    @mouseup.stop
    v-if="isModalVisible"
    :title="modalTitle"
    @hide="hide()"
    classes="mb-6"
  >
    <div class="pb-4">
      {{ $i18nPresenter.t("Upload a PDF, PPT or PPTX file. Up to 50MB and 100 slides.") }}
    </div>

    <div class="tab-content">
      <label
        for="upload-file"
        class="text-center"
      >
        <div v-if="isNotImporting">
          <div
            class="px-3 text-red-100 error-text"
            v-if="importError"
            v-html="importError"
          />

          <div>
            <aha-icon
              name="cloud-import"
              width="50"
              height="50"
              viewBox="0 0 55 40"
              class="text-primary-80"
            />
            <div class="py-2">
              <p class="pb-1">{{ $i18nPresenter.t("Drag and drop here") }}</p>
              <p>{{ $i18nPresenter.t("or") }}</p>
            </div>
          </div>
        </div>

        <div
          v-if="isUploading || isConverting"
          class="text-center"
        >
          <p class="text-lg font-bold" v-if="isUploading">{{ $i18nPresenter.t("Uploading...") }}</p>
          <p class="text-lg font-bold" v-if="isConverting">{{ $i18nPresenter.t("Converting...") }}</p>

          <div class="pt-2">
            {{ $i18nPresenter.t("Large presentations may take several minutes to finish.") }}
          </div>
          <div>
            <div class="py-2">
              <aha-spinner
                variant="dark"
              />
            </div>
            <aha-button @click="hide()">
              {{ $i18nPresenter.t("Close this window") }}
            </aha-button>
            <div class="pt-2">{{ $i18nPresenter.t("The import will continue in the background.") }}</div>
          </div>
        </div>

        <b-form-file
          v-model="file"
          :state="Boolean(file)"
          choose-label="Attachment2"
          accept=".ppt, .pptx, .pdf"
          id="upload-file"
          v-if="isNotImporting"
          drop-placeholder="drop image"
          :file-name-formatter="uploadFile"
          @mouseup.stop
        />
      </label>
      <aha-button v-if="isNotImporting">
        {{ $i18nPresenter.t("Browse File...") }}
        <b-form-file
          v-model="file"
          :state="Boolean(file)"
          choose-label="Attachment2"
          accept=".ppt, .pptx, .pdf"
          id="upload-file"
          v-if="isNotImporting"
          drop-placeholder="drop image"
          :file-name-formatter="uploadFile"
          @mouseup.stop
        />
      </aha-button>
      <div class="tab-tooltip">
        <p>{{ $i18nPresenter.t("Quiz generation is powered by AI") }}</p>
        <AIDocToQuizSecurityTooltip isGenerateFromDocModal />
      </div>
    </div>
  </aha-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Socket from '@/socket'
import importStatusConstant from '@/constant/importStatus'
import UploadApi from '@/api/upload'
import EventBus from '@/eventBus'
import utils from '@/util'

import AIDocToQuizSecurityTooltip from '@/components-v2/openAI/AIDocToQuizSecurityTooltip.vue'

const EVENT_BUS_DOC_TO_QUIZ_ERROR = 'doc-to-quiz: generating_error'

export default {
  components: {
    AIDocToQuizSecurityTooltip,
  },
  data() {
    return {
      file: null,
      isModalVisible: false,
      modalTitle: this.$i18nPresenter.t('Upload Document'),
      importStatusConstant,
      needGenerateQuiz: true,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      importStatus: (state) => state.generateFromDocImportStatus,
      importError: (state) => state.generateFromDocImportError,
      docToQuizUniqueRequestId: (state) => state.docToQuizUniqueRequestId,
    }),
    isNotImporting() {
      return this.importStatus === this.importStatusConstant.NO_IMPORT
    },
    isUploading() {
      return this.importStatus === this.importStatusConstant.UPLOADING
    },
    isConverting() {
      return this.importStatus === this.importStatusConstant.CONVERTING
    },
    isGeneratingQuizFromDoc() {
      return this.docToQuizUniqueRequestId && this.docToQuizUniqueRequestId.length > 0
    },
  },
  mounted() {
    this.show()
    EventBus.$on('doc-to-quiz: import_convert_error', (socketData) => {
      if (socketData.requestId !== this.docToQuizUniqueRequestId) return
      this.setGenerateFromDocImportErrorAction('Import failed.')
    })
  },
  destroyed() {
    EventBus.$off('doc-to-quiz: import_convert_error')
  },
  methods: {
    ...mapActions([
      'setGenerateFromDocModalStateAction',
      'setGenerateFromDocImportStatusAction',
      'setGenerateFromDocImportErrorAction',
      'setDocToQuizUniqueRequestIdAction',
    ]),
    async uploadFile(files) {
      try {
        if (!files || !files.length) return
        const file = files[0]
        this.setGenerateFromDocImportErrorAction(null)
        if (file && file.size > 52428800) {
          this.setGenerateFromDocImportErrorAction(this.importStatusConstant.ERROR_REACH_LIMIT)
          EventBus.$emit(EVENT_BUS_DOC_TO_QUIZ_ERROR)
          return
        }

        const ext = file.name.split('.').pop()
        if (ext.indexOf('ppt') === -1 && ext.indexOf('pdf') === -1) {
          this.setGenerateFromDocImportErrorAction(this.importStatusConstant.ERROR_UNSUPPORTED_FILE_TYPE)
          EventBus.$emit(EVENT_BUS_DOC_TO_QUIZ_ERROR)
          return
        }

        this.setGenerateFromDocImportStatusAction(this.importStatusConstant.UPLOADING)
        const formData = new FormData()
        formData.append('file', file)
        formData.append('socketId', Socket.getSocketId())
        formData.append('uniqueAccessCode', this.presentation.uniqueAccessCode)
        formData.append('fileSize', file.size)
        formData.append('fileName', file.name)
        if (this.needGenerateQuiz && !this.isGeneratingQuizFromDoc) {
          formData.append('needGenerateQuiz', this.needGenerateQuiz)
          const requestId = utils.generateRandomString()
          this.setDocToQuizUniqueRequestIdAction(`${requestId}-${this.presentation.id}`)
          formData.append('requestId', this.docToQuizUniqueRequestId)
          EventBus.$emit('doc-to-quiz: reset_all_results')
        }
        if (this.presentation.id) {
          formData.append('presentationId', this.presentation.id)
        }
        const headers = new Headers()
        headers.append('Content-Type', 'application/x-www-form-urlencoded')
        await UploadApi.uploadDocToQuiz(formData)
        this.setGenerateFromDocImportStatusAction(this.importStatusConstant.CONVERTING)
      } catch (error) {
        this.setGenerateFromDocModalStateAction(false)
        this.setGenerateFromDocImportStatusAction(this.importStatusConstant.NO_IMPORT)
        EventBus.$emit(EVENT_BUS_DOC_TO_QUIZ_ERROR, 'Upload file error')
      }
    },
    show() {
      this.isModalVisible = true
    },
    hide() {
      this.setGenerateFromDocImportErrorAction(null)
      this.setGenerateFromDocModalStateAction(false)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.tab-content {
  @apply border-2 border-dashed border-primary-90 px-4;
  @apply bg-primary-60 rounded py-8;
  @apply relative text-center;
}
.custom-file {
  @apply absolute inset-0 overflow-hidden opacity-0;
  & ::v-deep .custom-file-input {
    @apply w-full  h-full cursor-pointer;
  }
}
.tab-tooltip {
  @apply mb-4 pt-12 items-center justify-center relative;
  display: flex;
  &::v-deep {
    .doc-to-quiz__security-tooltip {
      @apply mb-0 pt-0;
    }
    .generate-from-doc__tooltip.aha-custom-tooltip.top {
      top: -3.5rem;
      left: 1.5rem;
    }
  }
}
</style>
